import { useQuery } from '@apollo/client';
import { Button, Field, Icon } from '@teamsnap/teamsnap-ui';
import { difference } from 'lodash';
import * as React from 'react';
import { useFieldArray } from 'react-hook-form';
import FeeModal, { UNLIMITED_FEE_MAX_AMOUNT } from '../FormFees/FeeModal';
import { FieldType } from '../types';
import { FormAdjustmentDefinition, RegistrationFormAdjustment } from './models';

import * as GetFormAdjustmentDefinitions from 'core/gql/queries/form-adjustment-definitions';

import './AddOnFees.scss';

const rangeExpr = /^.+ \(<span.*?>\$\d+(\.\d{2})? - (\$\d+(\.\d{2})?|Unlimited)<\/span>\)$/;

type AddOnFeesProps = {
  orgId: number;
  formId: number;
  question: FieldType;
  savedAddOnFees: RegistrationFormAdjustment[];
  formHook: any;
};

type FieldOption = {
  fee: number;
  id: number;
  key: string;
  maxQuantity: number;
  option: string;
};

type FeeSectionProps = {
  adjustmentDefinitions: FormAdjustmentDefinition[];
  formAdjustments: RegistrationFormAdjustment[];
  field: FieldOption;
  index: number;
  formHook: any;
  question: FieldType;
  formId: number;
  orgId: number;
  setLastEdited: (index: number) => void;
};

const searchFees = (fees, searchValue) => {
  const lowercaseFilter = searchValue?.toLowerCase();
  return fees?.filter((q) =>
    Object.values(q).reduce((acc, f) => acc || (f?.toString() || '').toLowerCase().includes(lowercaseFilter), false)
  );
};

const getFeeDisplayText = (name, min, max) =>
  `${name} (<span class="${Math.sign(min) === -1 ? 'u-colorNegative' : ''}">${Math.sign(min) === -1 ? '-' : ''}$${(
    Math.abs(min) / 100
  ).toFixed(2)}${
    max ? ' - ' + (UNLIMITED_FEE_MAX_AMOUNT === max / 100 ? 'Unlimited' : '$' + (max / 100).toFixed(2)) : ''
  }</span>)`;

const hasError = (index, name, errors) =>
  errors?.fees && errors?.fees[`${index}`] && errors.fees[`${index}`]?.[`${name}`];

const FeeSection = ({
  adjustmentDefinitions,
  formAdjustments,
  field,
  index,
  formHook,
  formId,
  orgId,
  question,
  setLastEdited,
}: FeeSectionProps) => {
  const [showList, setShowList] = React.useState(false);
  const [displayOptions, setDisplayOptions] = React.useState<
    { text: string; value: RegistrationFormAdjustment | FormAdjustmentDefinition }[]
  >([]);
  const [searchValue, setSearchValue] = React.useState<string | null>(null);

  const [adjustmentDefinitionToEdit, setAdjustmentDefinitionToEdit] = React.useState<FormAdjustmentDefinition>(null);
  const [adjustmentToEdit, setAdjustmentToEdit] = React.useState<RegistrationFormAdjustment>(null);
  const [showFeeModal, setShowFeeModal] = React.useState(false);
  const [selectedFeeText, setSelectedFeeText] = React.useState<string>('No Fee/Discount');

  const { register, unregister, watch, errors, setValue, getValues } = formHook;

  const watchFee = watch(`fees[${index}].fee`);

  const handleBodyClick = React.useCallback((e) => {
    e.stopPropagation();
    if (!showFeeModal) {
      const isTargetingPopup = e.target.closest(`#fees-${index}__container`) != null;
      if (!isTargetingPopup) {
        setShowList(false);
      }
    }
  }, []);

  const handleEditFee = (fee) => {
    if (fee.formAdjustmentDefinition) {
      setAdjustmentDefinitionToEdit(fee.formAdjustmentDefinition);
      setAdjustmentToEdit(fee);
    } else {
      const adjustment = formAdjustments.find(
        (a) =>
          a.formAdjustmentDefinition.id === fee.id &&
          +question.id === +a.formFieldId &&
          getValues(`fees[${index}].option`) === a.answerToMatch
      );
      setAdjustmentToEdit(adjustment);
      setAdjustmentDefinitionToEdit(fee);
    }

    setShowFeeModal(true);
  };

  const mapFeeOptions = (feeDefinition) => {
    const adjustment = formAdjustments.find(
      (a) =>
        a.formAdjustmentDefinition.id === feeDefinition.id &&
        +question.id === +a.formFieldId &&
        getValues(`fees[${index}].option`) === a.answerToMatch
    );
    let text = getFeeDisplayText(feeDefinition.name, feeDefinition.amount, feeDefinition.maxAmount);

    if (adjustment) {
      text = getFeeDisplayText(adjustment.formAdjustmentDefinition.name, adjustment.amount, adjustment.maxAmount);
    }

    return {
      text: text,
      value: adjustment ? adjustment : feeDefinition,
    };
  };

  const getSelecteFeeText = () => {
    if (watchFee === '') {
      setSelectedFeeText('No Fee/Discount');
    } else {
      const adjustment = formAdjustments.find(
        (a) =>
          a.formAdjustmentDefinition.id === +watchFee &&
          +question.id === +a.formFieldId &&
          getValues(`fees[${index}].option`) === a.answerToMatch
      );
      setSelectedFeeText(
        adjustment
          ? getFeeDisplayText(adjustment.formAdjustmentDefinition.name, adjustment.amount, adjustment.maxAmount)
          : adjustmentDefinitions.map(mapFeeOptions).find((f) => +f.value.id === +getValues(`fees[${index}].fee`))?.text
      );
    }
  };
  
  const isRangedFee = rangeExpr.test(selectedFeeText)
  
  React.useEffect(() => {
    if (!showList) {
      setSearchValue('');
      window.removeEventListener('click', handleBodyClick, false);
    } else {
      window.addEventListener('click', handleBodyClick, false);
    }
  }, [showList]);

  React.useEffect(() => {
    if (watchFee === '' || isRangedFee) {
      setValue(`fees[${index}].maxQuantity`, '');
      unregister(`fees[${index}].maxQuantity`);
    } else {
      const adjustment = formAdjustments.find((a) => a.answerToMatch === getValues(`fees[${index}].option`));
      setValue(`fees[${index}].maxQuantity`, adjustment?.maxQuantity ?? '1');
    }
    setShowList(false);
  }, [watchFee, selectedFeeText]);

  React.useEffect(() => {
    if (!searchValue || searchValue === '') {
      setDisplayOptions(adjustmentDefinitions.map(mapFeeOptions));
    } else {
      setDisplayOptions(searchFees(adjustmentDefinitions.map(mapFeeOptions), searchValue));
    }
    getSelecteFeeText();
  }, [searchValue, adjustmentDefinitions, formAdjustments, watchFee]);

  return (
    <React.Fragment key={field.key}>
      <input
        style={{ display: 'none' }}
        type="text"
        name={`fees[${index}].id`}
        defaultValue={field.id}
        ref={register()}
        disabled
      />
      <input
        style={{ display: 'none' }}
        type="text"
        name={`fees[${index}].option`}
        defaultValue={field.option}
        ref={register()}
        disabled
      />
      <input
        style={{ display: 'none' }}
        type="type"
        name={`fees[${index}].fee`}
        defaultValue={field.fee}
        ref={register()}
        disabled
      />
      <div id={`fees-${index}__container`} className="addon-fee__container">
        <div className="addon-fee__option">{getValues(`fees[${index}].option`)}</div>
        <div className="addon-fee__fee">
          <button
            onClick={() => setShowList(!showList)}
            className="addon-fee__fee-toggle"
            data-testid="addon-fee__fee-toggle"
            title={selectedFeeText}
            dangerouslySetInnerHTML={{ __html: selectedFeeText }}
          />
          <div className="fees-list__container">
            {showList && (
              <div className="fees-list">
                {(displayOptions.length > 5 || adjustmentDefinitions.length > 5) && (
                  <div className="fees-list__search">
                    <Field
                      type="input"
                      name="search"
                      testId="search-fee-input"
                      formFieldProps={{
                        placeholder: 'Search fee or discount name',
                        leftIcon: <Icon className="Icon" name="search" />,
                        inputProps: {
                          onChange: ({ target: { value } }) => {
                            setSearchValue(value);
                          },
                          value: searchValue || '',
                        },
                      }}
                    />
                  </div>
                )}
                {displayOptions.length > 0 ? (
                  <ul style={{ maxHeight: 200, overflowY: 'auto' }}>
                    <li>
                      <button
                        className="fee-item__select"
                        onClick={() => {
                          setValue(`fees[${index}].fee`, '');
                        }}
                        data-testid="no-fee-discount-button"
                      >
                        No Fee/Discount
                      </button>
                    </li>
                    {displayOptions.map((o, i) => (
                      <li key={o.value.id}>
                        <button
                          className="fee-item__select"
                          title={o.text}
                          onClick={() => {
                            setValue(`fees[${index}].fee`, o.value.id.toString());
                          }}
                          dangerouslySetInnerHTML={{ __html: o.text }}
                          data-testid={`fee-item__select-${i}`}
                        />
                        <Button
                          type="link"
                          icon="edit"
                          mods="fee-item__edit u-colorNeutral8"
                          onClick={() => handleEditFee(o.value)}
                          testId={`fee-item__edit-${i}`}
                        />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="u-textCenter u-padMd">
                    <p>
                      <strong>No results found</strong>
                    </p>
                  </div>
                )}

                <Button
                  color="primary"
                  icon="plus"
                  mods="u-padSidesXs btn-add-fee"
                  onClick={() => {
                    setAdjustmentDefinitionToEdit(null);
                    setAdjustmentToEdit(null);
                    setShowFeeModal(true);
                  }}
                  testId="add-fee-button"
                >
                  Add a new fee
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="addon-fee__quantity">
          {watchFee && watchFee != '' && !isRangedFee && (
            <div className="FieldGroup ">
              <label className="FieldGroup-label u-hiddenVisually" htmlFor={`fees[${index}].maxQuantity`}>
                Max Quantity per Participant
              </label>
              <input
                className={`Input ${hasError(index, 'maxQuantity', errors) ? 'u-borderColorNegative' : ''}`}
                type="number"
                name={`fees[${index}].maxQuantity`}
                id={`fees[${index}].maxQuantity`}
                ref={register({
                  max: {
                    value: 32000000,
                    message: 'Please add a smaller number.',
                  },
                  min: {
                    value: 1,
                    message: 'Please add a number greater than 0.',
                  },
                  required: 'Please enter a max quantity.',
                })}
                defaultValue={field.maxQuantity}
                data-testid={`fee-item__maxQuantity-${index}`}
              />
              {hasError(index, 'maxQuantity', errors) && (
                <p className="u-fontSizeSm u-colorNegative u-padSidesXs u-padTopXs">
                  {errors['fees'][`${index}`].maxQuantity.message}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      <FeeModal
        orgId={orgId}
        formId={formId}
        formFieldId={question.id}
        adjustmentDefinition={adjustmentDefinitionToEdit}
        adjustment={adjustmentToEdit}
        show={showFeeModal}
        currentOption={getValues(`fees[${index}].option`)}
        onClose={() => {
          setAdjustmentDefinitionToEdit(null);
          setAdjustmentToEdit(null);
          setShowFeeModal(false);
          setLastEdited(index);
        }}
      />
    </React.Fragment>
  );
};

const AddOnFees = ({ orgId, formId, question, savedAddOnFees, formHook }: AddOnFeesProps) => {
  const [hasFees, setHasFees] = React.useState(false);
  const [lastEdited, setLastEdited] = React.useState<number | null>(null);

  const { control, watch, setValue, unregister, getValues } = formHook;

  const { fields, append, remove, insert } = useFieldArray({
    control,
    name: 'fees',
    keyName: 'key',
  });

  const watchOptions = watch('options');

  const formAdjustmentDefinitions =
    useQuery(GetFormAdjustmentDefinitions.QUERY, {
      variables: { organizationId: orgId },
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only',
      skip: !orgId,
    })?.data?.formAdjustmentDefinitions?.filter((fad) => fad.type !== 'discount') || [];

  const showAddOnFeesSections = (show) => {
    setHasFees(show);
  };

  React.useEffect(() => {
    if (savedAddOnFees.length > 0 && savedAddOnFees.find((f) => f.formFieldId === question.id)) {
      setHasFees(true);
    }
  }, [savedAddOnFees, question]);

  React.useEffect(() => {
    const optionsList = watchOptions.split('\n').filter((f) => f !== '');
    // Populate with saved data
    if (hasFees && optionsList.length > 0 && fields.length === 0) {
      const feeSections = [];
      optionsList.forEach((option) => {
        const foundSavedFee = savedAddOnFees.find((f) => f.answerToMatch === option && f.formFieldId === question.id);
        feeSections.push({
          id: foundSavedFee?.id || '',
          option: option,
          fee: foundSavedFee?.id || '',
          maxQuantity: foundSavedFee?.maxQuantity || '1',
        } as FieldOption);
      });
      append(feeSections);
    }
    // Adding another answer option
    else if (optionsList.length > fields.length && fields.length !== 0) {
      const fieldsOptionsList = fields.map((f) => f.option);
      const newIndex = optionsList.indexOf(difference(optionsList, fieldsOptionsList)[0]);
      insert(newIndex, {
        id: '',
        option: optionsList.at(-1),
        fee: '',
        maxQuantity: '',
      });
    }
    // Removing an answer option
    else if (optionsList.length < fields.length && fields.length !== 0) {
      const fieldsOptionsList = fields.map((f) => f.option);
      const removedIndex = fieldsOptionsList.indexOf(difference(fieldsOptionsList, optionsList)[0]);
      remove(removedIndex);
    } else {
      if (hasFees) {
        optionsList.forEach((option, index) => {
          setValue(`fees[${index}].option`, option);
        });

        // Update form with created/updated RegistrationFormAdjustment
        fields.forEach((f, i) => {
          const adjustments = savedAddOnFees.filter(
            (a) => a.answerToMatch === f.option && a.formFieldId === question.id
          );
          // only update the last edited fee, there can be multiple fees saved for the same answer
          if (adjustments.length > 0 && i === lastEdited) {
            setValue(`fees[${i}].id`, adjustments[adjustments.length - 1].id);
            setValue(`fees[${i}].fee`, adjustments[adjustments.length - 1].id);
            setValue(`fees[${i}].maxQuantity`, adjustments[adjustments.length - 1].maxQuantity);
          }
        });
      } else {
        // Remove fees from form for current question if "No Fee/Discount".
        // This is needed so that all RegistrationFormAdjustment will be removed on save
        optionsList.forEach((_, index) => {
          unregister(`fees[${index}].id`);
          unregister(`fees[${index}].fee`);
          unregister(`fees[${index}].option`);
          unregister(`fees[${index}].maxQuantity`);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchOptions, hasFees, savedAddOnFees]);

  return (
    <>
      <div className="addon-fees__container">
        <Button
          type="button"
          mods={`u-borderNone u-borderRadiusNone ${hasFees ? 'u-colorNeutral7' : 'u-colorSuccess6 u-bgNeutral2'}`}
          onClick={() => showAddOnFeesSections(false)}
          isDisabled={question?.locked ?? false}
          testId="no-fee-discount-button"
        >
          {!hasFees && <Icon name="check" mods="u-fontSizeXs u-spaceRightXs" />}
          None
        </Button>
        {!question.locked && (
          <Button
            type="button"
            mods={`u-borderNone u-borderRadiusNone ${hasFees ? 'u-colorSuccess6 u-bgNeutral2' : 'u-colorNeutral7'}`}
            onClick={() => showAddOnFeesSections(true)}
            isDisabled={question?.locked ?? false}
            testId="apply-fee-discount-button"
          >
            {hasFees && <Icon name="check" mods="u-fontSizeXs u-spaceRightXs" />}
            Apply Fee/Discount
          </Button>
        )}
        {hasFees && (
          <div className="u-padSidesSm u-padBottomSm u-padTopXs u-bgNeutral2">
            {question?.options && (
              <>
                <div className="addon-fee__container">
                  <div className="addon-fee__heading">Answer</div>
                  <div className="addon-fee__heading">Fee/Discount</div>
                  <div className="addon-fee__heading">Max Quantity per Participant</div>
                </div>
                {fields.map((field, index) => (
                  <FeeSection
                    key={field.key}
                    adjustmentDefinitions={formAdjustmentDefinitions}
                    formAdjustments={savedAddOnFees.filter(
                      (f) => f.answerToMatch === getValues(`fees[${index}].option`) && f.formFieldId === question.id
                    )}
                    field={field as FieldOption}
                    index={index}
                    formHook={formHook}
                    question={question}
                    formId={formId}
                    orgId={orgId}
                    setLastEdited={setLastEdited}
                  />
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default AddOnFees;
